
import { defineComponent, markRaw } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import Utils from '@/utils/QSUtils';

import { miscSettingsStore } from '@/store';

import ModalConfirmation from '@/components/modals/Confirmation.vue';
import FormsService from '@/services/2.6/FormsService';
import FormsServiceV36 from '@/services/3.6/FormsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';

export default defineComponent({
    components: {
        draggable: VueDraggableNext,
        ModalConfirmation,
    },
    emits: ['callback'],
    props: {
        rules: {
            default: Object,
            required: true,
            type: Object,
        },
        fieldId: {
            default: 0,
            required: true,
            type: Number,
        },
        friendlyFieldName: {
            default: '',
            required: true,
            type: String,
        },
        originalValue: {
            default: '',
            required: true,
            type: String,
        },
    },
    data(props) {
        return {
            dataChangeTimeOut: 0,
            rulesPreviewText: '',
            list: props.rules || [],
            isDragging: false,
            modalConformation: {
                show: false,
                component: markRaw(ModalConfirmation),
                message: '',
                size: '',
                buttons: [] as any,
            },
        };
    },
    mounted() {
        // Append property ruleStatus for logging purposes
        for (const item in this.list) {
            this.list[item]['ruleStatus'] = 'noChange';
        }

        this.sortAsc();
        this.updateRulePreviewer();
        this.setListeners();
    },
    computed: {
        dragOptions() {
            return {
                animation: 0,
                group: 'description',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
    },
    methods: {
        callback() {
            this.$emit('callback', this.list);
        },
        deleteConfirmation(idx: number) {
            this.modalConformation.show = true;
            this.modalConformation.message = 'Are you sure you want to delete this rule?';
            this.modalConformation.buttons = [
                {
                    label: 'Yes',
                    css: 'btn-primary',
                    callback: () => {
                        this.updateRuleStatus(idx, 'deleted');
                    },
                },
                {
                    label: 'No',
                    css: 'btn-outline-secondary',
                },
            ];
        },
        addNewRule() {
            this.list.push({ ruleId: 0, priorityOrder: 999, textMatchOption: 1, textMatchCriteria: '', textEditOption: 1, textEditValue: '', ruleStatus: 'added' });
            this.finalProcess();
        },
        sortAsc: function () {
            return this.list.sort(function (a: { priorityOrder: number }, b: { priorityOrder: number }) {
                return a.priorityOrder - b.priorityOrder;
            });
        },
        isSelected(currentIdx: number, itemOrderIdx: number) {
            return Number(itemOrderIdx) === Number(currentIdx) ? true : false;
        },
        checkActiveRules() {
            // Check to see if aleast one item is NOT marked deleted.
            let itemIdx = this.list.findIndex((x: { ruleStatus: { toString: () => any } }) => x.ruleStatus !== 'deleted');
            if (itemIdx === -1) {
                return true;
            }
            return false;
        },
        resetIndex() {
            for (const idx in this.list) {
                this.list[idx]['priorityOrder'] = Number(idx);
            }
        },
        moveDeleted() {
            // Move all deleted items to the end of list so displayed row number is correct
            for (const idx in this.list) {
                if (this.list[idx]['ruleStatus'] === 'deleted') {
                    this.list.push(this.list.splice(idx, 1)[0]);
                }
            }
        },
        onTextMatchCriteriaChange(idx: number, value: any) {
            this.list[idx]['textMatchOption'] = Number(value);
            this.updateRuleStatus(idx, 'updated');
            this.ifFieldOptionAdditionalRules(idx, Number(value));
            this.finalProcess();
        },
        onTextEditValueChange(idx: number, value: any) {
            this.list[idx]['textEditOption'] = Number(value);
            this.updateRuleStatus(idx, 'updated');
            this.finalProcess();
        },
        onRunRulesOnChange(idx: number, value: any) {
            this.list[idx]['runRulesOn'] = Number(value);
            this.updateRuleStatus(idx, 'updated');
            this.finalProcess();
        },
        ifFieldOptionAdditionalRules(idx: number, value: number) {
            if (value === 3) {
                this.onTextEditValueChange(idx, 4);
            }

            if (value === 4) {
                this.onTextEditValueChange(idx, 1);
            }
        },
        isEditValueDisabled(selectedTextMatchCriteria: number): boolean {
            return selectedTextMatchCriteria === 3 ? true : false;
        },
        isEditValueOptionDisabled(selectedTextMatchCriteria: number): boolean {
            return selectedTextMatchCriteria === 4 ? true : false;
        },
        isMatchValueDisabled(selectedTextMatchCriteria: number, idx: number): boolean {
            if (selectedTextMatchCriteria === 3) {
                this.clearMatchValue(idx);
                return true;
            }
            return false;
        },
        clearMatchValue(idx: number) {
            this.list[idx]['textMatchCriteria'] = '';
        },
        onTextMatchCriteriaKeyup(idx: number, event: any) {
            this.list[idx]['textMatchCriteria'] = event.target.value;
            this.updateRuleStatus(idx, 'updated');
            this.finalProcess();
        },
        onTextEditValueKeyup(idx: number, event: any) {
            this.list[idx]['textEditValue'] = event.target.value;
            this.updateRuleStatus(idx, 'updated');
            this.finalProcess();
        },
        onDelete(idx: number) {
            this.list.splice(idx, 1);
            this.finalProcess();
        },
        onDrop() {
            this.finalProcess();
        },
        updateRuleStatus(idx: number, status: string) {
            if (status == 'updated' && this.list[idx]['ruleId'] > 0) {
                this.list[idx]['ruleStatus'] = 'updated';
                this.finalProcess();
            } else if (status == 'deleted' && this.list[idx]['ruleId'] > 0) {
                this.list[idx]['ruleStatus'] = 'deleted';
                this.finalProcess();
            } else if (status == 'deleted' && this.list[idx]['ruleId'] === 0) {
                // Newly created rules that have neved been saved can be removed without logging
                this.onDelete(idx);
            }
        },
        async updateRulePreviewer() {
            if (Utils.objectSize(this.list) === 0) return;
            clearTimeout(this.dataChangeTimeOut);
            this.dataChangeTimeOut = window.setTimeout(async () => {
                const preferredCasing = document.querySelector('input[name="casing"]:checked') as HTMLInputElement;
                const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();

                let result = "";
                const payload = {
                    fieldId: this.fieldId,
                    preferredCasing: preferredCasing.value,
                    fieldRules: this.list.filter((obj: { textMatchOption: number; textMatchCriteria: string; ruleStatus: string }) => (obj.textMatchOption === 3 && obj.textMatchCriteria === '' && obj.ruleStatus !== 'deleted') || (obj.textMatchCriteria !== '' && obj.ruleStatus !== 'deleted')),
                    value: this.originalValue,
                }

                if (miscSettingsStore.UADVersion === 2.6) {
                    result = await new FormsService(webEnvConfig).getDataFieldRulesPreviewer(payload)
                } else {

                    // Need to floor the fieldId because repeatable fields have a fieldId of a decimal value.  
                    // The backend expects the fieldId to be an integer.
                    payload.fieldId = Math.floor(payload.fieldId); 
                    result = await new FormsServiceV36(webEnvConfig).getDataFieldRulesPreviewer(payload)
                }

                this.rulesPreviewText = result;
            }, 500);
        },
        finalProcess() {
            this.moveDeleted();
            this.resetIndex();
            this.sortAsc();
            this.callback();
            this.updateRulePreviewer();
        },
        async setListeners() {
            // Listen for Preferred Case changes
            await Utils.sleep(1000); // Need to wait a few seconds for the PreferredCasing component to load.
            // Im sure there is a better way of doing this
            document.querySelectorAll('input[name="casing"]').forEach((input) => {
                input.addEventListener('change', () => {
                    this.updateRulePreviewer();
                });
            });
        },
    },
});
